<app-page-breadcrumb pageTitle="Plantillas de WhatsApp" />

<div class="mb-4 flex flex-wrap items-center justify-between gap-3">
  <p class="text-sm text-gray-500 dark:text-gray-400">
    Mensajes que WhatsApp te deja enviar cuando el cliente todavía no te escribió, o cuando pasó
    más de un día desde su último mensaje.
    <button
      type="button"
      (click)="helpOpen = !helpOpen"
      class="font-medium text-emerald-600 hover:underline dark:text-emerald-400"
    >
      {{ helpOpen ? 'Ocultar explicación' : '¿Por qué hacen falta?' }}
    </button>
  </p>
  <div class="flex gap-2">
    <button
      type="button"
      (click)="suggestionsOpen = !suggestionsOpen"
      class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
    >
      Plantillas listas para usar
      <span class="text-xs">{{ suggestionsOpen ? '▲' : '▼' }}</span>
    </button>
    <button
      type="button"
      (click)="load()"
      class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
    >
      Recargar
    </button>
    <button
      type="button"
      (click)="toggleForm()"
      class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
    >
      {{ formOpen ? 'Cancelar' : 'Escribir una nueva' }}
    </button>
  </div>
</div>

@if (helpOpen) {
  <div class="mb-4 rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-600 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
    <p class="mb-2">
      WhatsApp no deja que una empresa le escriba lo que quiera a un cliente. Solo puedes
      escribir libremente <span class="font-medium">durante las 24 h siguientes</span> a un
      mensaje del cliente. Fuera de esa ventana —y para escribirle a alguien por primera
      vez— hay que usar un <span class="font-medium">mensaje aprobado de antemano</span>: eso
      es una plantilla.
    </p>
    <p class="mb-2">
      Tú escribes el texto una vez, WhatsApp lo revisa (suele tardar minutos) y, una vez
      aprobado, puedes enviarlo a quien quieras. Las partes que cambian en cada envío —el
      nombre del cliente, una fecha, un monto— se dejan como huecos que el CRM rellena solo.
    </p>
    <p class="mb-0">
      Si es tu primera vez, no escribas ninguna: abre
      <span class="font-medium">«Plantillas listas para usar»</span> y créala con un clic.
    </p>
  </div>
}

@if (errorMessage) {
  <div class="mb-4 flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
    <span class="flex-1 whitespace-pre-wrap">{{ errorMessage }}</span>
    <button type="button" (click)="errorMessage = null" class="text-red-500 hover:text-red-700">×</button>
  </div>
}

@if (!loading && !errorMessage && templates.length === 0) {
  <div class="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
    Todavía no hay ninguna plantilla aprobada, así que <span class="font-medium">no se puede
    iniciar ninguna conversación</span> ni responder fuera de la ventana de 24 h. Crea una de
    las de abajo: WhatsApp suele revisarlas en minutos.
  </div>
}

@if (suggestionsOpen) {
  <div class="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
    <h2 class="mb-1 text-base font-semibold text-gray-900 dark:text-white">Plantillas listas para usar</h2>
    <p class="mb-4 text-xs text-gray-500 dark:text-gray-400">
      Ya escritas para lo que hace el CRM — reuniones, cotizaciones, tickets y seguimiento de
      contactos. <span class="font-medium">«Crear»</span> la manda a revisión tal cual;
      <span class="font-medium">«Cambiar el texto»</span> la abre para que la edites antes.
    </p>

    <div class="grid gap-3 md:grid-cols-2">
      @for (s of suggestions; track s.template.name) {
        <div class="flex flex-col rounded-lg border border-gray-200 p-4 dark:border-gray-800">
          <div class="mb-1 flex items-start justify-between gap-2">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ s.title }}</h3>
            <span class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
              {{ categoryLabel(s.template.category) }}
            </span>
          </div>
          <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">{{ s.description }}</p>

          <div class="mb-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/60">
            @if (s.template.headerText) {
              <p class="mb-1 text-xs font-semibold text-gray-900 dark:text-white">
                {{ s.template.headerText }}
              </p>
            }
            <p class="whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-300">
              {{ s.template.bodyText }}
            </p>
            @if (s.template.footerText) {
              <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                {{ s.template.footerText }}
              </p>
            }
          </div>

          @if (s.paramLabels.length) {
            <p class="mb-3 text-[11px] text-gray-500 dark:text-gray-400">
              El CRM rellena:
              @for (label of s.paramLabels; track $index) {
                <span class="mr-2 whitespace-nowrap">{{ label }}@if (!$last) {,}</span>
              }
            </p>
          }

          <div class="mt-auto flex justify-end gap-2">
            @if (isSuggestionCreated(s)) {
              <span class="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                Ya la tienes
              </span>
            } @else {
              <button
                type="button"
                (click)="useSuggestion(s)"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Cambiar el texto
              </button>
              <button
                type="button"
                (click)="createSuggestion(s)"
                [disabled]="!!creatingSuggestion"
                class="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                @if (creatingSuggestion === s.template.name) { Enviando… } @else { Crear }
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>
}

@if (formOpen) {
  <div class="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
    <h2 class="mb-1 text-base font-semibold text-gray-900 dark:text-white">Escribir una plantilla</h2>
    <p class="mb-5 text-xs text-gray-500 dark:text-gray-400">
      Al terminar la manda a revisión de WhatsApp. Mientras la revisan aparece abajo como
      «En revisión»; cuando la aprueben ya podrás enviarla.
    </p>

    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <!-- ── Los campos ─────────────────────────────────────────────────── -->
      <div class="space-y-5">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            1. ¿Para qué es este mensaje? <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            [(ngModel)]="titleInput"
            (ngModelChange)="onTitleChange()"
            placeholder="Recordatorio de cita"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
          @if (form.name && nameTaken) {
            <p class="mt-1 text-xs text-red-600 dark:text-red-400">
              Ya tienes una plantilla con este nombre. Ponle otro.
            </p>
          } @else if (form.name) {
            <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
              WhatsApp la guardará como <span class="font-mono">{{ form.name }}</span>
            </p>
          }
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            2. ¿Qué clase de mensaje es? <span class="text-red-500">*</span>
          </label>
          <div class="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              (click)="setCategory('UTILITY')"
              class="rounded-lg border p-3 text-left transition"
              [class.border-emerald-500]="form.category === 'UTILITY'"
              [class.bg-emerald-50]="form.category === 'UTILITY'"
              [class.dark:bg-emerald-900/20]="form.category === 'UTILITY'"
              [class.border-gray-300]="form.category !== 'UTILITY'"
              [class.dark:border-gray-700]="form.category !== 'UTILITY'"
            >
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Un aviso</span>
              <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                Sobre algo que el cliente ya tiene contigo: su cita, su pedido, su cotización,
                su reclamo.
              </span>
            </button>
            <button
              type="button"
              (click)="setCategory('MARKETING')"
              class="rounded-lg border p-3 text-left transition"
              [class.border-emerald-500]="form.category === 'MARKETING'"
              [class.bg-emerald-50]="form.category === 'MARKETING'"
              [class.dark:bg-emerald-900/20]="form.category === 'MARKETING'"
              [class.border-gray-300]="form.category !== 'MARKETING'"
              [class.dark:border-gray-700]="form.category !== 'MARKETING'"
            >
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Publicidad</span>
              <span class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                Ofertas, novedades, campañas. Solo a quien te dio permiso, y con una forma de
                darse de baja.
              </span>
            </button>
          </div>
          @if (form.category === 'UTILITY') {
            <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Ojo: si un «aviso» termina ofreciendo o vendiendo algo, WhatsApp lo rechaza.
            </p>
          }
        </div>

        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            3. Escribe el mensaje <span class="text-red-500">*</span>
          </label>
          <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Para lo que cambia en cada envío (el nombre, una fecha, un monto) usa el botón de
            abajo: deja un hueco que el CRM rellena solo.
          </p>

          <input
            type="text"
            [(ngModel)]="form.headerText"
            maxlength="60"
            placeholder="Título en negrita (opcional)"
            class="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />

          <textarea
            #bodyInput
            [(ngModel)]="form.bodyText"
            (ngModelChange)="onBodyChange()"
            rows="4"
            maxlength="1024"
            placeholder="Hola, te recordamos tu cita del..."
            class="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          ></textarea>

          <button
            type="button"
            (click)="insertParam()"
            class="mt-2 inline-flex items-center gap-1 rounded-lg border border-dashed border-emerald-400 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
          >
            + Insertar un dato que cambia
          </button>

          <input
            type="text"
            [(ngModel)]="form.footerText"
            maxlength="60"
            placeholder="Nota al pie en letra chica (opcional)"
            class="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
        </div>

        @if (paramCount > 0) {
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              4. Pon un ejemplo de cada dato <span class="text-red-500">*</span>
            </label>
            <p class="mb-2 text-xs text-gray-500 dark:text-gray-400">
              WhatsApp los pide para entender el mensaje al revisarlo. No se envían a nadie:
              son solo una muestra.
            </p>
            <div class="space-y-2">
              @for (e of form.bodyExamples; track $index) {
                <div class="flex items-center gap-2">
                  <span class="w-16 shrink-0 font-mono text-xs text-gray-400 dark:text-gray-500">
                    {{ placeholder($index + 1) }}
                  </span>
                  <input
                    type="text"
                    [(ngModel)]="form.bodyExamples[$index]"
                    placeholder="Por ejemplo: María"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              }
            </div>
          </div>
        }

        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Idioma
          </label>
          <select
            [(ngModel)]="form.language"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-48"
          >
            @for (l of languages; track l.code) {
              <option [value]="l.code">{{ l.label }}</option>
            }
          </select>
        </div>
      </div>

      <!-- ── Vista previa ───────────────────────────────────────────────── -->
      <div>
        <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Así lo verá el cliente
        </p>
        <div class="rounded-xl bg-[#e5ddd5] p-4 dark:bg-gray-800">
          @if (previewEmpty) {
            <p class="py-6 text-center text-xs text-gray-500 dark:text-gray-400">
              Escribe el mensaje y aparecerá aquí.
            </p>
          } @else {
            <div class="max-w-full rounded-lg rounded-tl-none bg-white p-3 shadow-sm dark:bg-gray-900">
              @if (form.headerText) {
                <p class="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                  {{ form.headerText }}
                </p>
              }
              <p class="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                {{ bodyPreview }}
              </p>
              @if (form.footerText) {
                <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">{{ form.footerText }}</p>
              }
            </div>
          }
        </div>
        <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Los huecos se muestran con los ejemplos que pusiste; en el envío real los rellena el CRM.
        </p>
      </div>
    </div>

    <div class="mt-6 flex justify-end gap-2">
      <button
        type="button"
        (click)="toggleForm()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
      >
        Cancelar
      </button>
      <button
        type="button"
        (click)="submit()"
        [disabled]="!canSubmit"
        class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        @if (saving) { Enviando… } @else { Enviar a revisión }
      </button>
    </div>
  </div>
}

<div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
  @if (loading) {
    <p class="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Cargando…</p>
  } @else if (templates.length === 0) {
    <div class="p-8 text-center">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        Esta cuenta todavía no tiene ninguna plantilla.
      </p>
      <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
        Crea una de las de arriba, o escribe la tuya con «Escribir una nueva». Si el negocio
        aún no conectó WhatsApp, hazlo primero en Configuración.
      </p>
    </div>
  } @else {
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-400">
          <tr>
            <th class="px-4 py-3">Plantilla</th>
            <th class="px-4 py-3">Estado</th>
            <th class="px-4 py-3">Mensaje</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          @for (t of templates; track t.name + t.language) {
            <tr class="border-b border-gray-100 last:border-0 dark:border-gray-800">
              <td class="px-4 py-3 align-top">
                <span class="font-medium text-gray-900 dark:text-white">{{ prettyName(t.name) }}</span>
                <span class="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">
                  {{ languageLabel(t.language) }} · {{ categoryLabel(t.category) }}
                </span>
              </td>
              <td class="px-4 py-3 align-top">
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [class]="statusClass(t.status)">
                  {{ statusLabel(t.status) }}
                </span>
                <span class="mt-1 block text-[11px] text-gray-400 dark:text-gray-500">
                  {{ statusHint(t.status) }}
                </span>
                @if (t.rejectedReason) {
                  <span class="mt-1 block text-[11px] text-red-600 dark:text-red-400">
                    {{ t.rejectedReason }}
                  </span>
                }
              </td>
              <td class="max-w-md px-4 py-3 align-top text-gray-600 dark:text-gray-300">
                <span class="line-clamp-2">{{ t.bodyText }}</span>
                @if (t.paramCount > 0) {
                  <span class="mt-0.5 block text-[11px] text-gray-400 dark:text-gray-500">
                    El CRM rellena {{ t.paramCount }} dato{{ t.paramCount === 1 ? '' : 's' }}.
                  </span>
                }
              </td>
              <td class="px-4 py-3 text-right align-top">
                <button
                  type="button"
                  (click)="remove(t)"
                  class="text-xs font-medium text-red-600 hover:underline dark:text-red-400"
                >
                  Borrar
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</div>
